import { useEffect, useRef, useState } from "react";
import BaseSearchClient, { BaseListResult } from "../BaseSearchClient";
import { EventListFilters } from "/Components/Pages/EventListPage/EventListPage";
import { NewsListFilters } from "/Components/Pages/NewsListPage/NewsListPage";

export interface BaseListFilters {}
export type FilterType = EventListFilters | NewsListFilters;

interface UseSearchPageOptions {
  client: BaseSearchClient;
  initFilters: FilterType;
  initList?: BaseListResult;
}

function useListPageFilters({
  client,
  initFilters,
  initList,
}: UseSearchPageOptions) {
  const [items, setItems] = useState(initList?.sectionItems);
  const [resultsCount, setResultsCount] = useState<number | undefined>(
    initList?.totalItems,
  );
  const [totalPages, setTotalPages] = useState(initList?.totalPages);
  const [filters, setFilters] = useState<FilterType>({
    ...initFilters,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const shouldPushState = useRef(true);
  const initialFilters = useRef(filters);

  useEffect(() => {
    if (filters === initialFilters.current) return;

    let superseded = false;
    const pushState = shouldPushState.current;
    shouldPushState.current = true;
    client.GetItems({ ...filters }).then((result) => {
      if (superseded) return;

      if ("error" in result) {
        setError(result.message);
        setLoading(false);
      } else {
        setLoading(false);
        setError(undefined);
        setItems(result.sectionItems);
        setResultsCount(
          result.totalItems ?? countResults(result.sectionItems),
        );
        setTotalPages(result.totalPages);
        if (pushState) {
          const newUrl = client.BuildUrlFromProps({ ...filters });
          window.history.pushState(null, "", newUrl);
        }
      }
    });

    return () => {
      superseded = true;
    };
  }, [filters]);

  const countResults = (sectionItems: any) => {
    if (!sectionItems) return 0;

    return sectionItems.reduce((total: number, section: any) => {
      return total + section.items.length;
    }, 0);
  };

  const setTab = (upcomingSelected: boolean) => {
    setFilters({ pageNumber: 1, upcomingEvents: upcomingSelected });
    setLoading(true);
  };

  const setPageNumber = (pageNum: number, upcomingSelected?: boolean) => {
    setFilters((current) => ({
      ...current,
      pageNumber: pageNum,
      ...(upcomingSelected !== undefined
        ? { upcomingEvents: upcomingSelected }
        : {}),
    }));
    setLoading(true);
  };

  const applyFilters = (nextFilters: Partial<FilterType>) => {
    setFilters((current) => ({
      ...current,
      ...nextFilters,
      pageNumber: nextFilters.pageNumber ?? 1,
    }));
    setLoading(true);
  };

  useEffect(() => {
    // Restores the filters from the URL when the user goes back or forward.
    const handlePopState = () => {
      const urlParams = client.GetParams();
      setFilters((current) => ({ ...current, ...urlParams }));
      shouldPushState.current = false;
      setLoading(true);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return {
    items,
    resultsCount,
    loading,
    error,
    filters,
    totalPages,
    setError,
    setFilters,
    setPageNumber,
    applyFilters,
    setTab,
  };
}

export default useListPageFilters;
